/**
 * Analytics Provider
 *
 * Tracks page views on route changes using React Router.
 * Respects cookie consent mechanism.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, hasAnalyticsConsent } from '@/lib/analytics/tracker';
import { updateSessionActivity } from '@/lib/analytics/sessionManager';

const AnalyticsProvider = () => {
  const location = useLocation();
  const lastTrackedPath = useRef<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Debounce to prevent duplicate tracking on quick navigation
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      const fullPath = location.pathname + location.search;

      // Don't track the same path twice in quick succession
      if (lastTrackedPath.current === fullPath) {
        return;
      }

      // Don't track admin pages
      if (location.pathname.startsWith('/admin')) {
        return;
      }

      // Check consent before tracking
      // TODO: Implement granular consent check if needed
      if (!hasAnalyticsConsent()) {
        return;
      }

      lastTrackedPath.current = fullPath;

      // Update session activity
      updateSessionActivity();

      // Track page view
      trackPageView(location.pathname, document.title);
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [location.pathname, location.search]);

  // Also listen to visibility changes to update session activity
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateSessionActivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return null;
};

export default AnalyticsProvider;
